// What the reader's HTML is made of: text, tables, lists, images and the inline SVG of the roll diagrams.
const HTML_TAGS = new Set(
	"a abbr b bdi bdo blockquote br caption cite code col colgroup dd del dfn div dl dt em figcaption figure h1 h2 h3 h4 h5 h6 hr i img ins kbd li mark ol p picture pre q s samp small source span strong sub sup table tbody td tfoot th thead time tr u ul var wbr".split(
		" ",
	),
);
const SVG_TAGS = new Set("svg g path circle ellipse rect line polyline polygon title desc defs".split(" "));
// Tags whose content is never shown, or that load or run something.
const DROPPED = new Set([
	"script",
	"style",
	"iframe",
	"object",
	"embed",
	"link",
	"meta",
	"base",
	"template",
	"noscript",
	"frame",
	"frameset",
	"applet",
	"audio",
	"video",
	"canvas",
]);

const GLOBAL = ["class", "title", "lang", "dir", "role", "aria-hidden", "aria-label", "style"];
const BY_TAG: Record<string, string[]> = {
	a: ["href"],
	img: ["src", "srcset", "sizes", "alt", "width", "height"],
	source: ["srcset", "sizes", "media", "type"],
	td: ["colspan", "rowspan", "headers"],
	th: ["colspan", "rowspan", "headers", "scope"],
	col: ["span"],
	colgroup: ["span"],
	ol: ["start", "reversed"],
	li: ["value"],
	time: ["datetime"],
};
const SVG_ATTRIBUTES = [
	"viewBox",
	"xmlns",
	"d",
	"fill",
	"fill-rule",
	"clip-rule",
	"stroke",
	"stroke-width",
	"stroke-linecap",
	"stroke-linejoin",
	"transform",
	"points",
	"cx",
	"cy",
	"r",
	"rx",
	"ry",
	"x",
	"y",
	"x1",
	"y1",
	"x2",
	"y2",
	"width",
	"height",
	"opacity",
	"focusable",
	"preserveAspectRatio",
];
const URL_ATTRIBUTES = new Set(["href", "src", "srcset"]);
const SAFE_URL = /^(?:https?:|mailto:|#|(?!\/\/)[^:]*$|data:image\/(?:png|jpe?g|gif|webp|svg\+xml);)/i;
const UNSAFE_STYLE = /url\s*\(|expression|@import|javascript:|behavior|-moz-binding/i;

function keep(el: Element, name: string, value: string): boolean {
	const svg = el.namespaceURI === "http://www.w3.org/2000/svg";
	const allowed = svg ? SVG_ATTRIBUTES.includes(name) || GLOBAL.includes(name) : GLOBAL.includes(name) || (BY_TAG[el.localName] ?? []).includes(name);
	if (!allowed) return false;
	if (name === "style") return !UNSAFE_STYLE.test(value);
	return !URL_ATTRIBUTES.has(name) || value.split(",").every((part) => SAFE_URL.test(part.trim().split(/\s+/)[0] ?? ""));
}

function clean(parent: ParentNode) {
	for (const el of [...parent.children]) {
		const tag = el.localName;
		if (tag === "iframe") {
			const src = el.getAttribute("src") ?? "";
			const link = document.createElement("a");
			if (/^https?:/i.test(src)) {
				link.setAttribute("href", src);
				link.textContent = src;
			}
			el.replaceWith(link);
		} else if (DROPPED.has(tag) || tag.includes("-")) {
			// A custom element (turbo-frame, turbo-stream-source) runs when it reaches the page, with its children.
			el.remove();
		} else if (el.namespaceURI === "http://www.w3.org/2000/svg" ? SVG_TAGS.has(tag) : HTML_TAGS.has(tag)) {
			for (const { name, value } of [...el.attributes]) if (!keep(el, name, value)) el.removeAttribute(name);
			clean(el);
		} else {
			// An element the reader never produces keeps its content and loses itself.
			clean(el);
			el.replaceWith(...el.childNodes);
		}
	}
}

// Server HTML is drawn inside the editor's page, where a Stimulus attribute would run a controller (this one
// included) and a custom element would load what its attributes name. A preview keeps what the reader's
// markup is made of and nothing that acts: tags and attributes are allow-listed, iframes become links, and
// the parsing happens in an inert template, so nothing runs before it is clean. The stored Markdown is
// never touched, only what is drawn from it.
export function sanitizePreview(html: string): DocumentFragment {
	const template = document.createElement("template");
	template.innerHTML = html;
	clean(template.content);
	return template.content;
}
